import { useMemo } from 'react';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import { Typography } from '@mui/material';
import {
  Flex,
  FlexSpace,
  Menu,
  IconButton,
} from '@elementary/common-ui/components';
import { Option } from '@elementary/common-ui/components/Menu/Menu';
import GenerateAIButton from '@elementary/common-ui/components/GenerateAIButton';
import { ReactComponent as MoreIcon } from '@elementary/common-ui/assets/more.svg';
import { ReactComponent as LineageIcon } from '@elementary/common-ui/assets/20x20/lineage.svg';
import { ReactComponent as CatalogIcon } from '@elementary/common-ui/assets/20x20/catalog.svg';
import {
  INCIDENT_ID,
  TREE_NODE,
} from '@elementary/common-ui/constants/urlParams';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import useNavigateWithEnvParam from '@elementary/common-ui/hooks/useNavigateWithEnvParam';
import { fontSize, fontWeight } from '@elementary/common-ui/style/utils';
import { isIncidentReadOnly } from '../../utils/incidentState';
import useIncidentDetails from '../../hooks/useIncidentDetails';

const IncidentDrawerTitle = () => {
  const [searchParams] = useSearchParams();
  const { data: account } = useAccount();
  const navigate = useNavigateWithEnvParam();

  const incidentId = searchParams.get(INCIDENT_ID);
  const { data: incident } = useIncidentDetails(incidentId ?? undefined);

  const isReadOnly = isIncidentReadOnly(incident);

  const shouldShowAIButton = useMemo(
    () => !!incidentId && !!incident && !isReadOnly,
    [incidentId, incident, isReadOnly],
  );
  const firstAssetId = incident?.monitors?.[0]?.asset?.id;

  const handleMenuChange = (value: string) => {
    if (!firstAssetId) return;

    const params = new URLSearchParams();
    params.set(TREE_NODE, JSON.stringify({ id: firstAssetId }));

    switch (value) {
      case 'lineage':
        navigate(`/report/lineage?${params.toString()}`);
        break;
      case 'catalog':
        navigate(`/catalog?${params.toString()}`);
        break;
    }
  };

  const menuOptions: Option[] = firstAssetId
    ? [
        {
          value: 'lineage',
          label: 'Show asset in lineage',
          icon: <LineageIcon />,
        },
        {
          value: 'catalog',
          label: 'Show asset in catalog',
          icon: <CatalogIcon />,
        },
      ]
    : [];

  return (
    <FlexSpace sx={{ width: '100%', alignItems: 'center', pr: 1 }}>
      <Typography
        sx={{ fontSize: fontSize('lg'), fontWeight: fontWeight('semibold') }}
      >
        Incident Overview
      </Typography>
      <Flex sx={{ gap: 1, alignItems: 'center' }}>
        {shouldShowAIButton && (
          <GenerateAIButton
            suggestions={['Find the root cause', 'Show downstream impact']}
            context={
              incidentId
                ? {
                    incidentIds: [incidentId],
                    displayLabels: incident?.title
                      ? [incident.title]
                      : undefined,
                  }
                : undefined
            }
            disabled={!account?.ai_features_enabled}
          >
            Investigate with AI
          </GenerateAIButton>
        )}

        {menuOptions.length > 0 && (
          <Menu
            menuButton={
              <IconButton>
                <MoreIcon />
              </IconButton>
            }
            options={menuOptions}
            onChange={handleMenuChange}
            closeOnChange
          />
        )}
      </Flex>
    </FlexSpace>
  );
};

export default IncidentDrawerTitle;
